// File: src/pages/Admin/Dashboard.js

import React, { useState, useEffect, useRef } from 'react'
import { auth, db } from '../../firebase'
import {
  collection,
  getDocs,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  orderBy
} from 'firebase/firestore'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'

export default function AdminDashboard() {
  const [user, setUser]       = useState(null)
  const [admins, setAdmins]   = useState([])
  const [profiles, setProfiles] = useState([])
  const [tasks, setTasks]     = useState([])
  const [form, setForm]       = useState({
    title: '', description: '', assignedTo: '', template: ''
  })
  const navigate = useNavigate()
  const assignFormRef = useRef(null)

  // 1) Templates
  const templates = [
    { id: 'ProfileSubmission', label: 'Profile Submission' }
    // …add more here…
  ]

  // 2) Super-admin guard
  useEffect(() => {
    return onAuthStateChanged(auth, async u => {
      if (!u) return navigate('/admin/login')
      const snap = await getDoc(doc(db, 'admins', u.email))
      if (!snap.exists() || snap.data().role !== 'superadmin') {
        await signOut(auth)
        return navigate('/admin/login')
      }
      setUser(u)
    })
  }, [navigate])

  // 3) Load all admins + their profile metadata
  useEffect(() => {
    ;(async () => {
      const snap = await getDocs(collection(db, 'admins'))
      setAdmins(
        snap.docs.map(d => {
          const data = d.data()
          return {
            email:       d.id,
            role:        data.role,
            legalName:   data.legalName   || '',
            team:        data.team        || '',
            jobPosition: data.jobPosition || '',
            supervisor:  data.supervisor  || '',
            subordinates:data.subordinates|| [],
            dateJoined:  data.dateJoined  || ''
          }
        })
      )
    })()
  }, [])

  // 4) Pending profile submissions
  useEffect(() => {
    if (!user) return
    const unsub = onSnapshot(collection(db, 'profiles'), snap =>
      setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )
    return unsub
  }, [user])

  // 5) All tasks
  useEffect(() => {
    if (!user) return
    const q = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'))
    return onSnapshot(q, snap =>
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )
  }, [user])

  // 6) Assign a task
  const handleAssign = async e => {
    e.preventDefault()
    await addDoc(collection(db, 'tasks'), {
      title:       form.title,
      description: form.description,
      assignedTo:  form.assignedTo,
      template:    form.template,
      status:      'pending',
      progress:    0,
      createdAt:   new Date()
    })
    setForm({ title:'', description:'', assignedTo:'', template:'' })
  }

  // 7) Task operations
  const handleTaskUpdate = (id, updates) =>
    updateDoc(doc(db, 'tasks', id), updates)

  const handleTaskDelete = id =>
    deleteDoc(doc(db, 'tasks', id))

  // 8) Profile submission approval
  const handleApproveProfile = id =>
    updateDoc(doc(db, 'profiles', id), {
      status:     'approved',
      reviewedAt: new Date(),
      reviewerUid:user.uid
    })
  const handleRejectProfile = async id => {
    const comments = prompt('Rejection comments:', '')
    await updateDoc(doc(db, 'profiles', id), {
      status:           'rejected',
      reviewedAt:       new Date(),
      reviewerUid:      user.uid,
      reviewerComments: comments
    })
  }

  if (!user) return <p>Loading…</p>

  // build pending-task counts
  const pendingCounts = tasks.reduce((acc, t) => {
    if (t.status === 'pending') acc[t.assignedTo] = (acc[t.assignedTo]||0) + 1
    return acc
  }, {})

  // helper to update an admin field
  const saveAdminField = async (email, field, value) => {
    const docRefAdmin = doc(db, 'admins', email)
    const updates = { [field]: field === 'subordinates'
      ? value.split(',').map(s=>s.trim()).filter(s=>s)
      : value }
    await updateDoc(docRefAdmin, updates)
    // update local state
    setAdmins(a =>
      a.map(x =>
        x.email === email ? { ...x, [field]: updates[field] } : x
      )
    )
  }

  return (
    <div style={{ padding:20 }}>
      <h2>Super-Admin Dashboard</h2>

      {/* ── EMPLOYEES GRID ────────────────────────── */}
      <section style={{ marginBottom:40 }}>
        <h3>Employees &amp; Pending Tasks</h3>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {[
                'Email',
                'Legal Name',
                'Team',
                'Position',
                'Supervisor',
                'Subordinates',
                'Date Joined',
                'Pending Tasks',
                'Actions'
              ].map(h => (
                <th
                  key={h}
                  style={{ border:'1px solid #ddd', padding:8, textAlign:'left' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {admins.map(a => (
              <tr key={a.email}>
                {/* Email (read-only) */}
                <td style={{ border:'1px solid #ddd', padding:8 }}>
                  {a.email}
                </td>

                {/* Editable fields */}
                <td style={{ border:'1px solid #ddd', padding:4 }}>
                  <input
                    value={a.legalName}
                    onChange={e =>
                      setAdmins(xs =>
                        xs.map(x =>
                          x.email===a.email
                            ? { ...x, legalName: e.target.value }
                            : x
                        )
                      )
                    }
                    onBlur={e =>
                      saveAdminField(a.email, 'legalName', e.target.value)
                    }
                    style={{ width:'100%', border:'none', padding:4 }}
                  />
                </td>

                <td style={{ border:'1px solid #ddd', padding:4 }}>
                  <input
                    value={a.team}
                    onChange={e=>
                      setAdmins(xs =>
                        xs.map(x=>
                          x.email===a.email
                            ? { ...x, team:e.target.value }
                            : x
                        )
                      )
                    }
                    onBlur={e=>
                      saveAdminField(a.email,'team',e.target.value)
                    }
                    style={{ width:'100%', border:'none', padding:4 }}
                  />
                </td>

                <td style={{ border:'1px solid #ddd', padding:4 }}>
                  <input
                    value={a.jobPosition}
                    onChange={e=>
                      setAdmins(xs=>
                        xs.map(x=>
                          x.email===a.email
                            ? { ...x, jobPosition:e.target.value }
                            : x
                        )
                      )
                    }
                    onBlur={e=>
                      saveAdminField(a.email,'jobPosition',e.target.value)
                    }
                    style={{ width:'100%', border:'none', padding:4 }}
                  />
                </td>

                <td style={{ border:'1px solid #ddd', padding:4 }}>
                  <input
                    value={a.supervisor}
                    onChange={e=>
                      setAdmins(xs=>
                        xs.map(x=>
                          x.email===a.email
                            ? { ...x, supervisor:e.target.value }
                            : x
                        )
                      )
                    }
                    onBlur={e=>
                      saveAdminField(a.email,'supervisor',e.target.value)
                    }
                    style={{ width:'100%', border:'none', padding:4 }}
                  />
                </td>

                <td style={{ border:'1px solid #ddd', padding:4 }}>
                  <input
                    value={a.subordinates.join(', ')}
                    onChange={e=>
                      setAdmins(xs=>
                        xs.map(x=>
                          x.email===a.email
                            ? { ...x, subordinates:e.target.value.split(',').map(s=>s.trim()) }
                            : x
                        )
                      )
                    }
                    onBlur={e=>
                      saveAdminField(a.email,'subordinates',e.target.value)
                    }
                    style={{ width:'100%', border:'none', padding:4 }}
                  />
                </td>

                <td style={{ border:'1px solid #ddd', padding:4 }}>
                  <input
                    type="date"
                    value={a.dateJoined}
                    onChange={e=>
                      setAdmins(xs=>
                        xs.map(x=>
                          x.email===a.email
                            ? { ...x, dateJoined:e.target.value }
                            : x
                        )
                      )
                    }
                    onBlur={e=>
                      saveAdminField(a.email,'dateJoined',e.target.value)
                    }
                    style={{ width:'100%', border:'none', padding:4 }}
                  />
                </td>

                {/* Pending Tasks */}
                <td style={{ border:'1px solid #ddd', padding:8 }}>
                  {pendingCounts[a.email] || 0}
                </td>

                {/* Assign button */}
                <td style={{ border:'1px solid #ddd', padding:8 }}>
                  <button onClick={() => {
                    setForm(f=>({ ...f, assignedTo:a.email }))
                    assignFormRef.current?.scrollIntoView({ behavior:'smooth' })
                  }}>
                    Assign Task
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── ASSIGN TASK FORM ───────────────────────── */}
      <section ref={assignFormRef} style={{ marginBottom:40, maxWidth:400 }}>
        <h3>Assign a Task</h3>
        <form onSubmit={handleAssign}>
          <input
            placeholder="Title"
            value={form.title}
            onChange={e=>setForm(f=>({...f,title:e.target.value}))}
            required
            style={{ width:'100%', marginBottom:8, padding:6 }}
          />
          <textarea
            placeholder="Description"
            rows={3}
            value={form.description}
            onChange={e=>setForm(f=>({...f,description:e.target.value}))}
            style={{ width:'100%', marginBottom:8, padding:6 }}
          />
          <select
            value={form.assignedTo}
            onChange={e=>setForm(f=>({...f,assignedTo:e.target.value}))}
            required
            style={{ width:'100%', marginBottom:8, padding:6 }}
          >
            <option value="" disabled>Assign to…</option>
            {admins.map(a=>(
              <option key={a.email} value={a.email}>
                {a.email}
              </option>
            ))}
          </select>
          <select
            value={form.template}
            onChange={e=>setForm(f=>({...f,template:e.target.value}))}
            required
            style={{ width:'100%', marginBottom:8, padding:6 }}
          >
            <option value="" disabled>Choose template…</option>
            {templates.map(t=>(
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <button type="submit" style={{ padding:'8px 12px' }}>
            Assign
          </button>
        </form>
      </section>

      {/* ── PENDING PROFILES ───────────────────────── */}
      <section style={{ marginBottom:40 }}>
        <h3>Pending Profile Submissions</h3>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['Name','Owner UID','Status','Actions'].map(h=>(
                <th
                  key={h}
                  style={{ border:'1px solid #ddd', padding:8, textAlign:'left' }}
                >{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {profiles.filter(p=>p.status==='pending').map(p=>(
              <tr key={p.id}>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{p.name}</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{p.ownerUid}</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{p.status}</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>
                  <button onClick={()=>handleApproveProfile(p.id)}>
                    Approve
                  </button>
                  <button
                    onClick={()=>handleRejectProfile(p.id)}
                    style={{ marginLeft:8 }}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── ALL TASKS ─────────────────────────────── */}
      <section>
        <h3>All Tasks</h3>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {[
                'Template','Title','Assigned To',
                'Status','Progress','Actions'
              ].map(h=>(
                <th
                  key={h}
                  style={{ border:'1px solid #ddd', padding:8, textAlign:'left' }}
                >{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tasks.map(t=>(
              <tr key={t.id}>
                <td style={{ border:'1px solid #ddd', padding:8 }}>
                  {templates.find(tp=>tp.id===t.template)?.label||t.template}
                </td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{t.title}</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{t.assignedTo}</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{t.status}</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>{t.progress}%</td>
                <td style={{ border:'1px solid #ddd', padding:8 }}>
                  <button
                    onClick={()=>handleTaskUpdate(t.id,{status:'done',progress:100})}
                  >Done</button>
                  <button
                    onClick={()=>handleTaskUpdate(t.id,{status:'pending',progress:0})}
                    style={{ marginLeft:8 }}
                  >Reset</button>
                  <button
                    onClick={()=>handleTaskDelete(t.id)}
                    style={{ marginLeft:8, color:'red' }}
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
